import { Field, Form, Formik } from "formik";
import React, { FC, useState } from "react";
import Modal from 'react-modal';
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { requiredValidation } from "../../utilities/Validation";
import closeIcon from '../../images/close.svg';
import Label from "../label/label";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useDialog } from "../dialog/DialogContext";
import { getErrorMessage } from "../../services/utilities/ServiceError";

interface VerifyPasswordModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onVerify: (password: string) => Promise<void>;
}

const VerifyPasswordModal: FC<VerifyPasswordModalProps> = ({ isOpen, setIsOpen, onVerify }) => {
    const { translate } = useLocale();
    const [showPassword, setShowPassword] = useState(false);
    const dialog = useDialog();

    const initialValues = {
        password: ''
    }

    const handleSubmit = async (fields) => {
        try {
            await onVerify(fields.password);
            setIsOpen(false);
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }
    }

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float-modal fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none overflow-hidden">
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={handleSubmit}>
            {
                ({ errors }) => {
                    return <Form>
                        <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                            <div className="flex flex-row">
                                <span className="pl-3 font-bold font text-white">{translate.byKey(TranslationKeys.verifyPassword)} </span>
                            </div>
                            <img src={closeIcon} onClick={() => setIsOpen(false)} className="cursor-pointer mr-2"/>
                        </div>

                        <div className="pt-3 pl-4 pr-0" style={{ width: '300px' }}>
                            <span className='font-bold pt-3 text-portal-blue-primary'>{translate.byKey(TranslationKeys.enterYourPassword)}: </span>
                            <div className="flex flex-col flex-1 pr-3 mt-2">
                                <div className="flex flex-row justify-between">
                                    <Label htmlFor='password' className="text-xs text-gray-600 mt-1" required>
                                        {translate.byKey(TranslationKeys.password)}:
                                    </Label>
                                    {errors.password ? <span className='text-red-600 text-xs'>{errors.password}</span> : null}
                                </div>
                                <div className="relative">
                                    <Field
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        validate={(value) => requiredValidation(value, translate)}
                                        className="flex flex-row items-center pr-10 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full"/>
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                        <FontAwesomeIcon
                                            className="text-gray-600 cursor-pointer"
                                            onClick={() => setShowPassword(!showPassword)}
                                            icon={showPassword ? faEye : faEyeSlash}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row pb-3 justify-end bg-white">
                            <div className="flex flex-row mt-4 mr-5 space-x-2">
                                <button type="button" onClick={() => setIsOpen(false)} className="secondary secondary-inverse py-1 px-5">
                                    {translate.byKey(TranslationKeys.cancel)}
                                </button>
                                <button id='submitBtn' className="secondary secondary-enabled py-1 px-5" type='submit'>
                                    {translate.byKey(TranslationKeys.verify)}
                                </button>
                            </div>
                        </div>
                    </Form>
                }
            }
        </Formik>
    </Modal>
}

export default VerifyPasswordModal;